import React, { useMemo } from 'react';

import { Link } from 'react-router-dom';
import moment from 'moment';
import findLastIndex from 'lodash/findLastIndex';

import Tooltip from '../../../../../Tooltip';

export const createTooltip = (bill) => {
  if (bill.status !== 'paid') {
    return (
      <Tooltip>
        <p className="not-italic">This bill is not yet paid</p>
      </Tooltip>
    );
  }

  if (bill.isPaidOutside) {
    return (
      <Tooltip>
        <p className="not-italic">
          This bill was paid from outside the borrower&apos;s impound account.
        </p>
      </Tooltip>
    );
  }

  return '';
};

const createDescription = (row, bills) => {
  if (row.type !== 'P') {
    return [];
  }

  const { date } = row;

  const scheduledBills = bills?.filter((bill) =>
    moment(bill.due_date).isSame(date, 'month')
  );

  // Use previous year bills if no exact date bills are found
  /* if (!scheduledBills?.length) {
    scheduledBills = bills?.filter(
      (bill) => moment(bill.due_date).format('MM') === moment(date).format('MM')
    );
  } */

  const result = scheduledBills?.map((bill) => {
    return {
      description: `${bill.type}${
        bill._vendor?.name ? ` - ${bill._vendor?.name}` : ''
      }${bill.status ? ` - ${bill.status}` : ''}`,
      link: `/loans/${bill.loan_id}/bills/${bill.id}?tab=Bill`,
      isPaidOutside:
        !bill._paid_from_impound &&
        !bill._paid_to_impound &&
        row.status === 'paid',
      status: bill.status,
    };
  });

  return result;
};

export default function useInvoices(impoundSchedule, bills) {
  // First we sort the invoices by sequence
  const sortedInvoices = useMemo(() => {
    if (!impoundSchedule._invoices) {
      return [];
    }

    const result = impoundSchedule?._invoices?.toSorted(
      (a, b) => +a.sequence - +b.sequence
    );

    return result;
  }, [impoundSchedule]);

  // Find the last paid invoice index
  const lastPaidInvoiceIndex = useMemo(() => {
    if (!sortedInvoices) {
      return -1;
    }

    const lastIndex = findLastIndex(
      sortedInvoices,
      (invoice) => invoice.status === 'paid'
    );

    return lastIndex;
  }, [sortedInvoices]);

  // After the last paid invoice, we show only 18 projected invoices
  const invoicesAfterLastPaid = useMemo(() => {
    const result = sortedInvoices
      .slice(lastPaidInvoiceIndex + 1, lastPaidInvoiceIndex + 1 + 24)
      .map((invoice, index) => {
        return {
          ...invoice,
          impound_projected_balance: 0,
          impound_projected_cushion: '-',
          impound_cushion: '-',
          style: index > 11 ? 'hidden' : '',
          isProjected: true, // this is a projected invoice
        };
      });

    return result;
  }, [sortedInvoices, lastPaidInvoiceIndex]);

  const invoicesWithProjection = useMemo(() => {
    let result = [...sortedInvoices];

    // If there are invoices after the last paid invoice, we add projected row between last paid invoice and the projected invoices
    if (
      lastPaidInvoiceIndex !== -1 &&
      lastPaidInvoiceIndex < sortedInvoices.length - 1
    ) {
      result = [
        ...sortedInvoices.slice(0, lastPaidInvoiceIndex + 1),
        {
          sequence: 'PROJECTED',
          date: '',
          impound_collected: '',
          _paid_impounds: { amount_paid: '' },
          impound_projected_balance: '',
          impound_projected_cushion: '',
          impound_cushion: '',
        },
        ...invoicesAfterLastPaid,
      ];
    }

    return result;
  }, [invoicesAfterLastPaid, lastPaidInvoiceIndex, sortedInvoices]);

  // If there are no invoices after the last paid invoice, we show all invoices without projected row

  const invoicesWithDescription = useMemo(() => {
    const invoices = invoicesWithProjection.map((invoice) => {
      const scheduledBills = createDescription(invoice, bills);

      return {
        ...invoice,
        descriptionData: scheduledBills.map((bill) => {
          return {
            link: bill.link,
            description: bill.description,
            isPaidOutside: bill.isPaidOutside,
            status: bill.status,
          };
        }),
        description: scheduledBills.length ? (
          <div className="flex flex-col">
            {scheduledBills.map((bill) => {
              return (
                <Link
                  className="text-blue-600 underline flex items-center gap-1"
                  to={bill.link}
                >
                  {bill.description}
                  {createTooltip(bill)}
                </Link>
              );
            })}
          </div>
        ) : (
          ''
        ),
      };
    });

    return invoices;
  }, [invoicesWithProjection, bills]);

  return invoicesWithDescription;
}
